import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    DestroyRef,
    inject,
    OnDestroy,
    OnInit,
    Signal,
    signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { select } from '@ngxs/store';
import { filter, map, tap } from 'rxjs';
import { EscalationStatus, IEscalation, IUser } from '@shs-digi-care-monorepo/interfaces';
import { UserState } from '../../states/user/user.state';
import { EscalationService } from '../../services/escalation.service';
import { AppsyncService } from '../../services/appsync.service';
import { EscalationsSubscriptionResult } from '../../services/appsync.model';
import { ActionRequiredDialogComponent } from '../action-required-dialog/action-required-dialog.component';
import {
    ActionRequiredDialogData,
    ActionRequiredDialogResult
} from '../action-required-dialog/action-required-dialog.model';

@Component({
    selector: 'app-action-required',
    templateUrl: './action-required.component.html',
    imports: [TranslateModule, RouterModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ActionRequiredComponent implements OnInit, OnDestroy {
    private readonly escalationService = inject(EscalationService);
    private readonly appsyncService = inject(AppsyncService);
    private readonly dialogService = inject(MatDialog);
    private readonly destroyRef = inject(DestroyRef);

    protected me: Signal<IUser | null> = select(UserState.me);
    protected isTaskOwner: Signal<boolean> = select(UserState.isTaskOwner);
    protected gid = computed(() => this.me()?.gid ?? '');

    protected showActionRequired = signal(false);

    public ngOnInit(): void {
        this.loadEscalations();
        this.subscribeToEscalations();
        this.subscribeToSnoozedEscalations();
    }

    public ngOnDestroy(): void {
        this.showActionRequired.set(false);
    }

    private loadEscalations(): void {
        this.escalationService
            .getEscalations(0)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((escalations) => {
                    this.showActionRequired.set(this.escalationService.hasActionableEscalations(escalations));
                })
            )
            .subscribe();
    }

    private subscribeToEscalations(): void {
        const gid = this.gid();
        if (!gid) return;

        const query = `subscription SubscribeToUserEscalations {
            subscribe2channel(name: "users/${gid}/escalations") { name data }
        }`;

        this.appsyncService
            .listenTo<EscalationsSubscriptionResult>(query)
            .then((result) => {
                result
                    .pipe(
                        takeUntilDestroyed(this.destroyRef),
                        filter((msg) => !!msg),
                        map((msg) => msg.data.subscribe2channel.data),
                        tap((data) => {
                            const escalation = JSON.parse(data).data as IEscalation;
                            this.openActionRequiredDialog(escalation.id, escalation.resourceId, escalation.status);
                        })
                    )
                    .subscribe();
            })
            .catch((error) => console.error('Error in escalation subscription:', error));
    }

    private subscribeToSnoozedEscalations(): void {
        const gid = this.gid();
        if (!gid) return;

        const query = `subscription SubscribeToUserEscalations {
            subscribe2channel(name: "users/${gid}/expired-escalations") { name data }
        }`;

        this.appsyncService
            .listenTo<EscalationsSubscriptionResult>(query)
            .then((result) => {
                result
                    .pipe(
                        takeUntilDestroyed(this.destroyRef),
                        filter((msg) => !!msg),
                        map((msg) => msg.data.subscribe2channel.data),
                        tap((data) => {
                            const escalation = JSON.parse(data).data as IEscalation;
                            this.openActionRequiredDialog(escalation.id, escalation.resourceId, escalation.status);
                        })
                    )
                    .subscribe();
            })
            .catch((error) => console.error('Error in expired escalation subscription:', error));
    }

    private openActionRequiredDialog(escalationId: string, requestId: string, status: EscalationStatus): void {
        this.dialogService
            .open<ActionRequiredDialogComponent, ActionRequiredDialogData, ActionRequiredDialogResult>(
                ActionRequiredDialogComponent,
                {
                    width: '600px',
                    disableClose: true,
                    data: { escalationId, requestId, status }
                }
            )
            .afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap((result) => {
                    if (result?.escalationId) {
                        this.escalationService
                            .snoozeEscalation(escalationId)
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe();
                        this.showActionRequired.set(true);
                    }
                })
            )
            .subscribe();
    }
}
